import React, { useCallback, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	View,
	ViewSettingsButton,
	ViewSettingsSyncFromLocalStorageWrapper,
} from '@atlassian/jira-software-view-settings';
import ViewSettingsPopup from '@atlassian/jira-software-view-settings/src/ui';
import { VIEW_SETTINGS_PANEL } from '../../../../model/panels/constants';
import type { PanelType } from '../../../../model/panels/types';
import type { CardEntryGroup } from '../../../../model/types';
import { useBacklogSelector } from '../../../../state';
import { collapseAllCardGroups, setExpandedCardGroups } from '../../../../state/actions/card-list';
import { getIsCompanyManaged } from '../../../../state/selectors/app';
import {
	backlogHasDueDate,
	backlogHasEstimates,
	backlogHasPriority,
	backlogHasStoryPoints,
	getCardExtraFields,
} from '../../../../state/selectors/cards';
import { getConfig, isCriticalDataLoaded } from '../../../../state/selectors/config';
import { useAllCardGroups } from '../../../../state/state-hooks/use-all-card-groups';
import { useIsViewSettingsPanelOpen } from '../../../../state/state-hooks/use-is-view-settings-panel-open';

export const isViewSettingAsPanelEnabled = () => {
	const [expConfig] = UNSAFE_noExposureExp('jsw_additional_card_fields_in_view_settings');
	return (
		ff('jsw.perf.additional-card-fields-in-view-settings') &&
		expConfig.get('cohort', 'not-enrolled') === 'variation'
	);
};

export type Props = {
	rapidViewId: number;
	isClassic: boolean;
	showEpicAsPanel: boolean;
	isVersionsEnabled: boolean;
	onTogglePanel: (panelType: PanelType) => void;
};

export const BacklogViewSettings = (props: Props) => {
	const { rapidViewId, isClassic, showEpicAsPanel, isVersionsEnabled, onTogglePanel } = props;
	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);
	const config = useBacklogSelector(getConfig);
	const epicCustomName = config.hierarchyNomenclature?.firstLevelName;
	const allCardGroups: CardEntryGroup[] = useAllCardGroups();
	const isDataLoaded = useBacklogSelector(isCriticalDataLoaded);
	const cardExtraFields = useBacklogSelector(getCardExtraFields);
	const hasCardsWithPriority = useBacklogSelector(backlogHasPriority);
	const isDueDateEnabled = useBacklogSelector(backlogHasDueDate);
	const hasCardsWithStoryPoint = useBacklogSelector(backlogHasStoryPoints);
	const hasCardsWithEstimate = useBacklogSelector(backlogHasEstimates);
	const isViewSettingPanelOpen = useIsViewSettingsPanelOpen();
	const showFilterBarToggle = isCompanyManaged;

	const areAllSubtasksExpanded: boolean = useMemo(
		() => allCardGroups.every((card) => !card.isCollapsed),
		[allCardGroups],
	);
	const areAllSubtasksCollapsed: boolean = useMemo(
		() => allCardGroups.every((card) => card.isCollapsed),
		[allCardGroups],
	);

	const handleExpandSubtasks = useCallback(() => {
		setExpandedCardGroups(allCardGroups.map((card) => card.parentId.toString()));
	}, [allCardGroups]);

	const handleCollapseSubtasks = useCallback(() => {
		collapseAllCardGroups();
	}, []);

	const handleViewSettingPanelToggle = useCallback(() => {
		onTogglePanel(VIEW_SETTINGS_PANEL);
	}, [onTogglePanel]);

	const shouldShowSubtasks: boolean = useMemo(() => !isEmpty(allCardGroups), [allCardGroups]);

	return isViewSettingAsPanelEnabled() ? (
		isDataLoaded && (
			<ViewSettingsSyncFromLocalStorageWrapper
				boardId={rapidViewId}
				view={View.BACKLOG}
				cardExtraFields={cardExtraFields}
				isVersionsEnabled={isVersionsEnabled}
				isEstimateEnabled={hasCardsWithStoryPoint || hasCardsWithEstimate}
				showEpicAsPanel={showEpicAsPanel}
				isDueDateEnabled={!isCompanyManaged && isDueDateEnabled}
				isPriorityEnabled={hasCardsWithPriority}
			>
				<ViewSettingsButton
					isSelected={isViewSettingPanelOpen}
					onClick={handleViewSettingPanelToggle}
				/>
			</ViewSettingsSyncFromLocalStorageWrapper>
		)
	) : (
		<ViewSettingsPopup
			view={View.BACKLOG}
			boardId={rapidViewId}
			isClassic={isClassic}
			areSubtasksEnabled={isCompanyManaged && shouldShowSubtasks}
			onSubtasksExpandAll={handleExpandSubtasks}
			onSubtasksCollapseAll={handleCollapseSubtasks}
			areAllSubtasksExpanded={areAllSubtasksExpanded}
			areAllSubtasksCollapsed={areAllSubtasksCollapsed}
			showEpicAsPanel={showEpicAsPanel}
			epicCustomName={epicCustomName}
			isVersionsEnabled={isVersionsEnabled}
			showFilterBarToggle={showFilterBarToggle}
		/>
	);
};
